/**
 * 符数（fu）计算
 *
 * 符数决定最终点数，规则细节极多。核心公式：
 *   基本符（20）+ 面子符 + 雀头符 + 听牌符 + 自摸符
 *   然后进位到十位（七对子除外，固定 25 符）
 *
 * 特殊规则：
 *   · 七对子：固定 25 符（不进位）
 *   · 平和自摸：20 符（不加自摸符）
 *   · 副露平和型：20 符（不成立平和役，但仍 20 符）
 *   · 平和荣和：30 符（20 + 门清荣和 10 符）
 *
 * 面子符：
 *   中张刻子（2-8）：明刻 2 / 暗刻 4 / 明杠 8 / 暗杠 16
 *   幺九刻子（1/9/字）：明刻 4 / 暗刻 8 / 明杠 16 / 暗杠 32
 * 雀头符：
 *   役牌（三元牌/场风/自风）：+2
 * 听牌符：
 *   单骑 / 坎张 / 边张：+2
 * 自摸符：
 *   自摸和牌：+2（平和自摸除外）
 */

import { isHonor, isTerminal } from './tile.js';
import type { Decomposition, Mentsu } from './agari.js';
import { isKoutsu, isShuntsu, isKantsu, isAnkou } from './yaku/checkers.js';
import type { WinContext } from './yaku/types.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 符数明细项 */
export interface FuItem {
  /** 项目名 */
  readonly label: string;
  /** 符数 */
  readonly fu: number;
}

/** 符数计算结果 */
export interface FuResult {
  /** 最终符数（已进位） */
  readonly fu: number;
  /** 明细 */
  readonly items: readonly FuItem[];
  /** 特殊说明（如"七对子固定 25 符"） */
  readonly note?: string;
  /** 进位前小计 */
  readonly subtotal: number;
}

// ════════════════════════════════════════════════════════════════
// 主入口
// ════════════════════════════════════════════════════════════════

/**
 * 计算符数
 *
 * @param d 和牌分解
 * @param ctx 场况（需要 winMethod / winTile / seatWind / roundWind）
 * @param isPinfu 是否成立平和（影响自摸符）
 * @param isClosed 是否门清
 */
export function calculateFu(
  d: Decomposition,
  ctx: WinContext,
  isPinfu = false,
  isClosed = true,
): FuResult {
  // ── 七对子：固定 25 符 ──
  if (d.form === 'chiitoitsu') {
    return {
      fu: 25,
      items: [{ label: '七对子固定', fu: 25 }],
      note: '七对子固定 25 符（不进位）',
      subtotal: 25,
    };
  }

  // ── 国士无双：役满，符数无意义 ──
  if (d.form === 'kokushi') {
    return {
      fu: 25,
      items: [{ label: '国士无双', fu: 25 }],
      note: '国士无双为役满，符数不计',
      subtotal: 25,
    };
  }

  const items: FuItem[] = [];
  let subtotal = 0;

  // ── 基本符 20 ──
  items.push({ label: '基本符', fu: 20 });
  subtotal += 20;

  // ── 面子符 ──
  for (const m of d.mentsu) {
    const fu = mentsuFu(m);
    if (fu > 0) {
      items.push({ label: mentsuLabel(m), fu });
      subtotal += fu;
    }
  }

  // ── 雀头符（役牌雀头 +2）──
  if (d.pair !== null) {
    const pairFu = pairFuValue(d.pair, ctx);
    if (pairFu > 0) {
      items.push({ label: `雀头（${pairLabel(d.pair, ctx)}）`, fu: pairFu });
      subtotal += pairFu;
    }
  }

  // ── 听牌符（单骑/坎张/边张 +2）──
  const waitFu = waitFuValue(d, ctx);
  if (waitFu > 0) {
    items.push({ label: `听牌型（${waitFuName(d, ctx)}）`, fu: waitFu });
    subtotal += waitFu;
  }

  // ── 自摸符（+2，平和自摸除外）──
  if (ctx.winMethod === 'tsumo' && !isPinfu) {
    items.push({ label: '自摸', fu: 2 });
    subtotal += 2;
  }

  // ── 门清荣和符（+10）──
  // 注意：平和荣和也是 30 符（20 + 10），这是标准规则
  if (ctx.winMethod === 'ron' && isClosed) {
    items.push({ label: '门清荣和', fu: 10 });
    subtotal += 10;
  }

  // ── 副露平和型：20 符（不加门清荣和）──
  // 平和自摸：20 符（不加自摸符）
  // 这两种情况已在上面通过 isPinfu / isClosed 处理

  // ── 进位到十位 ──
  const fu = roundUpToTen(subtotal);
  if (fu !== subtotal) {
    items.push({ label: '进位', fu: fu - subtotal });
  }

  return { fu, items, subtotal };
}

// ════════════════════════════════════════════════════════════════
// 面子符
// ════════════════════════════════════════════════════════════════

/**
 * 单个面子的符数
 *
 * 中张（2-8）：明刻 2 / 暗刻 4 / 明杠 8 / 暗杠 16
 * 幺九（1/9/字）：明刻 4 / 暗刻 8 / 明杠 16 / 暗杠 32
 */
export function mentsuFu(m: Mentsu): number {
  // 顺子 0 符
  if (isShuntsu(m)) return 0;

  const yaochu = isYaochuMentsu(m) ? 2 : 1; // 幺九翻倍
  const open = m.open;

  if (isKantsu(m)) {
    return open ? 8 * yaochu : 16 * yaochu;
  }
  // 刻子
  return open ? 2 * yaochu : 4 * yaochu;
}

/** 面子是否由幺九牌组成（刻子类） */
function isYaochuMentsu(m: Mentsu): boolean {
  return isHonor(m.start) || isTerminal(m.start);
}

/** 面子符的标签 */
function mentsuLabel(m: Mentsu): string {
  const types: Record<string, string> = {
    shuntsu: '顺子',
    koutsu: '刻子',
    kantsu: '杠子',
  };
  const yaochu = isYaochuMentsu(m) ? '幺九' : '中张';
  const open = m.open ? '明' : '暗';
  return `${yaochu}${open}${types[m.kind]}`;
}

// ════════════════════════════════════════════════════════════════
// 雀头符
// ════════════════════════════════════════════════════════════════

/**
 * 雀头符：役牌（三元牌 / 场风 / 自风）+2；连风牌（场风=自风）雀头 +4
 */
export function pairFuValue(pair: number, ctx: WinContext): number {
  if (pair >= 31 && pair <= 33) return 2; // 三元牌
  const windIdx: Record<string, number> = { east: 27, south: 28, west: 29, north: 30 };
  let fu = 0;
  if (pair === windIdx[ctx.roundWind]) fu += 2; // 场风
  if (pair === windIdx[ctx.seatWind]) fu += 2; // 自风（连风牌时二者叠加 = 4）
  return fu;
}

/** 雀头是否为役牌 */
function isYakuhaiPair(pair: number, ctx: WinContext): boolean {
  // 三元牌 31-33
  if (pair >= 31 && pair <= 33) return true;
  // 风牌 27-30
  const windIdx: Record<string, number> = { east: 27, south: 28, west: 29, north: 30 };
  if (pair === windIdx[ctx.roundWind]) return true;
  if (pair === windIdx[ctx.seatWind]) return true;
  return false;
}

function pairLabel(pair: number, ctx: WinContext): string {
  if (pair >= 31) return '三元牌';
  return pair === 27 + Object.keys({ east: 0, south: 1, west: 2, north: 3 }).indexOf(ctx.roundWind)
    ? '场风'
    : '自风';
}

// ════════════════════════════════════════════════════════════════
// 听牌符
// ════════════════════════════════════════════════════════════════

/**
 * 听牌符：单骑（雀头听）+2 / 坎张 +2 / 边张 +2 / 两面 0
 */
export function waitFuValue(d: Decomposition, ctx: WinContext): number {
  return waitType(d, ctx.winTile!, ctx.waitReading) === 'ryanmen' ? 0 : 2;
}

export type FuWaitType = 'ryanmen' | 'kanchan' | 'penchan' | 'tanki';

/** 判断听牌型（用于符数） */
export function waitType(
  d: Decomposition,
  winTile: number,
  override?: FuWaitType,
): FuWaitType {
  if (override) return override; // 高点法：强制待ち读法
  // 单骑：和了牌是雀头
  if (d.pair === winTile) return 'tanki';

  for (const m of d.mentsu) {
    if (m.open) continue;
    if (m.kind === 'shuntsu') {
      const start = m.start;
      const end = start + 2;
      const startFace = (start % 9) + 1;
      if (winTile === start + 1) return 'kanchan';
      if (winTile === start) return startFace === 7 ? 'penchan' : 'ryanmen';
      if (winTile === end) return startFace === 1 ? 'penchan' : 'ryanmen';
    }
  }
  return 'ryanmen';
}

function waitFuName(d: Decomposition, ctx: WinContext): string {
  const t = waitType(d, ctx.winTile!, ctx.waitReading);
  const names: Record<FuWaitType, string> = {
    ryanmen: '两面',
    kanchan: '坎张',
    penchan: '边张',
    tanki: '单骑',
  };
  return names[t];
}

// ════════════════════════════════════════════════════════════════
// 工具
// ════════════════════════════════════════════════════════════════

/** 进位到十位（如 22 → 30，30 → 30，31 → 40） */
export function roundUpToTen(n: number): number {
  return Math.ceil(n / 10) * 10;
}

/** 符数 → 可读字符串 */
export function formatFu(r: FuResult): string {
  const parts = r.items.map((i) => `${i.label}(${i.fu})`);
  return `${parts.join(' + ')} = ${r.fu} 符${r.note ? ` [${r.note}]` : ''}`;
}
